import { api } from '../../api';
import {
  ApiError,
  CustomerProfile,
  HangoutCreated,
  HangoutInvite,
  HangoutInviteInput,
  HangoutMessage,
  HangoutMessagePage,
  HangoutParticipant,
  HangoutVenue,
  InviteResponseStatus,
  NotificationList,
  PublicHangoutInvite
} from './types';

// Normalises axios errors into { status, code, message } so pages can map codes to error states.
export function toApiError(err: unknown): ApiError {
  const e = err as { response?: { status?: number; data?: { message?: string; code?: string } }; message?: string };
  if (!e?.response) return { status: 0, code: 'network', message: 'Network error. Check your connection and try again.' };
  return {
    status: e.response.status ?? 0,
    code: e.response.data?.code,
    message: e.response.data?.message ?? 'Something went wrong. Please try again.'
  };
}

// ---------- Customer accounts ----------

export interface OtpRequestResult {
  message: string;
  isNewCustomer: boolean;
  resendAfterSeconds: number;
  devCode?: string;
}

export interface CustomerLogin {
  accessToken: string;
  expiresAtUtc: string;
  customer: CustomerProfile;
  isNewCustomer: boolean;
}

export const requestOtp = async (mobile: string) => (await api.post<OtpRequestResult>('/api/customer/otp/request', { mobile })).data;

export const verifyOtp = async (input: { mobile: string; code: string; displayName?: string; ageConfirmed: boolean }) =>
  (await api.post<CustomerLogin>('/api/customer/otp/verify', input)).data;

export const getCustomerProfile = async () => (await api.get<CustomerProfile>('/api/customer/me')).data;

// ---------- Notifications ----------

export const getNotifications = async () => (await api.get<NotificationList>('/api/notifications')).data;
export const markAllNotificationsRead = async () => { await api.post('/api/notifications/read'); };
export const markNotificationRead = async (id: number) => { await api.post(`/api/notifications/${id}/read`); };

// ---------- Hangouts ----------

const base = '/api/hangout-invites';

export const createHangoutInvite = async (input: HangoutInviteInput) => (await api.post<HangoutCreated>(base, input)).data;
export const updateHangoutInvite = async (id: number, input: HangoutInviteInput) => (await api.put<HangoutInvite>(`${base}/${id}`, input)).data;
export const publishHangoutInvite = async (id: number) => (await api.post<HangoutInvite>(`${base}/${id}/publish`)).data;
export const cancelHangoutInvite = async (id: number) => (await api.post<HangoutInvite>(`${base}/${id}/cancel`)).data;
export const getHangoutInvite = async (id: number) => (await api.get<HangoutInvite>(`${base}/${id}`)).data;
export const getMyHangoutInvites = async () => (await api.get<HangoutInvite[]>(`${base}/mine`)).data;

export const getPublicHangoutInvite = async (publicToken: string, ref?: string | null) =>
  (await api.get<PublicHangoutInvite>(`${base}/public/${encodeURIComponent(publicToken)}`, { params: ref ? { ref } : undefined })).data;

export const respondToHangoutInvite = async (id: number, status: InviteResponseStatus) =>
  (await api.post<HangoutInvite>(`${base}/${id}/respond`, { status })).data;
export const leaveHangout = async (id: number) => (await api.post<HangoutInvite>(`${base}/${id}/leave`)).data;

export const getInviteParticipants = async (id: number) => (await api.get<HangoutParticipant[]>(`${base}/${id}/participants`)).data;
export const approveParticipant = async (id: number, participantId: number) =>
  (await api.post<HangoutParticipant>(`${base}/${id}/participants/${participantId}/approve`)).data;
export const declineParticipant = async (id: number, participantId: number) =>
  (await api.post<HangoutParticipant>(`${base}/${id}/participants/${participantId}/decline`)).data;
export const removeParticipant = async (id: number, participantId: number) =>
  (await api.delete<HangoutParticipant>(`${base}/${id}/participants/${participantId}`)).data;
export const blockParticipant = async (id: number, participantId: number) => { await api.post(`${base}/${id}/participants/${participantId}/block`); };
export const reportParticipant = async (id: number, participantId: number, reason: string) => {
  await api.post(`${base}/${id}/participants/${participantId}/report`, { reason });
};

export const getInviteMessages = async (id: number, opts: { cursor?: number; after?: number; limit?: number } = {}) =>
  (await api.get<HangoutMessagePage>(`${base}/${id}/messages`, { params: opts })).data;
export const sendInviteMessage = async (id: number, message: string) =>
  (await api.post<HangoutMessage>(`${base}/${id}/messages`, { message })).data;

export const getHangoutVenue = async (venueId: number) =>
  (await api.get<HangoutVenue>(`/api/hangout-invites/venue/${venueId}`)).data;
